// Line items — taller rows; SKU lives inside the product cell. Each real SKU
// row carries a click-only "View product" button that expands an inline detail
// panel (fetched on open) directly under its row — ONE open at a time, closes
// via the button, ✕ or Esc. Replaces the old hover-driven Product Information
// panel (which caused layout-shift glitches at different screen sizes).
import { Fragment, useEffect, useState } from "react";
import { ClipboardList, Package, Plus, Trash2, Settings2, BadgePercent, SlidersHorizontal, Info, X, FileText } from "lucide-react";
import api from "../../../lib/api";
import { GBP, unitFromCostMargin, tierUnitFromBase, marginFromUnit, bandOf, effectiveCost, exVatFromInc, lineWebDiscPct } from "../_quoteHelpers";
import { T2Panel } from "../_t2";
import AvailabilityChip from "./AvailabilityChip";
import MarginStudio from "./MarginStudio";
import PricingRulesModal from "./PricingRulesModal";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Module-level cache so re-opening a panel is instant and never refetches.
const _pcache = {};

// Qty / Unit £ / Cost stepper — −/+ buttons OUTSIDE a bigger number box.
function StepBox({ value, onChange, step, min = 0, decimals = 0, testid, disabled, suffix }) {
  const v = Number(value) || 0;
  const clamp = (n) => Math.max(min, decimals ? round2(n) : Math.round(n));
  const [txt, setTxt] = useState(null); // raw text while typing — avoids "07" after clearing
  return (
    <div className="qb-stepbox" style={{ opacity: disabled ? 0.5 : 1 }}>
      <button type="button" className="t2-step qb-step-v" disabled={disabled} onClick={() => onChange(clamp(v + step))} data-testid={`${testid}-inc`} aria-label="increase">+</button>
      <div style={{ position: "relative", width: "100%" }}>
        <input type="number" step={step} min={min} value={txt ?? (value ?? 0)} disabled={disabled}
          onChange={(e) => { setTxt(e.target.value); onChange(e.target.value === "" ? 0 : Number(e.target.value)); }}
          onFocus={(e) => e.target.select()}
          onBlur={() => setTxt(null)}
          className="t2-input t2-mono t2-step-input"
          style={{ padding: suffix ? "5px 16px 5px 4px" : "5px 4px", textAlign: "center", fontSize: 14.5, fontWeight: 800, width: "100%" }}
          data-testid={testid} />
        {suffix && <span className="t2-mono" style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", fontSize: 11, fontWeight: 700, color: "var(--t2-grey3)", pointerEvents: "none" }}>{suffix}</span>}
      </div>
      <button type="button" className="t2-step qb-step-v" disabled={disabled} onClick={() => onChange(clamp(v - step))} data-testid={`${testid}-dec`} aria-label="decrease">−</button>
    </div>
  );
}

// One commercial stat tile.
function Tile({ label, value, accent }) {
  return (
    <div style={{ background: "var(--t2-ink)", border: "1px solid var(--t2-border)", borderRadius: 9, padding: "10px 12px" }}>
      <div className="t2-mono" style={{ fontSize: 15, fontWeight: 900, color: accent || "var(--t2-white)", lineHeight: 1.15 }}>{value}</div>
      <div style={{ fontSize: 9.5, color: "var(--t2-grey3)", textTransform: "uppercase", letterSpacing: ".07em", marginTop: 3 }}>{label}</div>
    </div>
  );
}

function SkeletonBlock({ h = 58, w = "100%" }) {
  return <div style={{ height: h, width: w, borderRadius: 9, background: "linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.08), rgba(255,255,255,.04))", backgroundSize: "200% 100%", animation: "t2shimmer 1.2s infinite" }} />;
}

// Clean the BigCommerce custom_fields down to attributes useful to a sales agent:
// drop blanks, datasheet URLs, placeholder "1"/true/false, brand dup, and dedupe.
function cleanAttrs(cf) {
  const seen = new Set(); const out = [];
  for (const f of cf || []) {
    const name = String(f.name || "").trim();
    const val = String(f.value == null ? "" : f.value).trim();
    if (!name || !val) continue;
    if (/datasheet/i.test(name)) continue;
    if (/^https?:\/\//i.test(val)) continue;
    if (val === "1" || /^(true|false)$/i.test(val)) continue;
    if (/^(brand|lbs_exvat)$/i.test(name)) continue;
    const key = name.toLowerCase().replace(/\s+/g, " ");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([name, val]);
  }
  return out.slice(0, 15);
}

// Inline product detail — fetched on open. Mirrors the old Product Information
// panel content, trimmed to what a sales agent actually needs.
function ProductDetailPanel({ sku, onClose }) {
  const [state, setState] = useState({ loading: true, product: null });
  useEffect(() => {
    let alive = true;
    if (!sku) { setState({ loading: false, product: null }); return; }
    if (Object.prototype.hasOwnProperty.call(_pcache, sku)) { setState({ loading: false, product: _pcache[sku] }); return; }
    setState({ loading: true, product: null });
    api.get(`/lab/products?search=${encodeURIComponent(sku)}&limit=5`)
      .then(({ data }) => {
        const items = data.items || [];
        const p = items.find((x) => String(x?.BCProduct?.sku || "").toLowerCase() === String(sku).toLowerCase()) || items[0] || null;
        _pcache[sku] = p;
        if (alive) setState({ loading: false, product: p });
      })
      .catch(() => { if (alive) setState({ loading: false, product: null }); });
    return () => { alive = false; };
  }, [sku]);

  const { loading, product } = state;
  const bc = product?.BCProduct || {};
  const tm = product?.TMProduct || {};
  const sellEx = exVatFromInc(bc.price);
  const sellInc = bc.price;
  const cost = Number(bc.cost_price);
  const listMargin = (sellEx != null && cost > 0) ? ((sellEx - cost) / sellEx) * 100 : null;
  const attrs = cleanAttrs(bc.custom_fields);
  const datasheets = (bc.custom_fields || []).filter((f) => /^https?:\/\//i.test(String(f.value || "")));
  const namedTags = (bc.tags || []).filter((t) => typeof t === "string" && t.trim());

  return (
    <div data-testid="product-detail-panel"
      style={{ position: "sticky", left: 0, maxWidth: "calc(100vw - 32px)", boxSizing: "border-box", background: "rgba(255,104,32,0.045)", boxShadow: "inset 3px 0 0 var(--t2-orange)", borderTop: "1px solid var(--t2-border)", padding: "16px 18px 18px" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9.5, color: "var(--t2-grey3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>
            <Package className="w-3 h-3" /> Product information
            {bc.sku && <span className="t2-mono" style={{ color: "var(--t2-orange-ink)", letterSpacing: 0 }}>{bc.sku}</span>}
          </div>
          {!loading && product && (
            <div style={{ fontSize: 14.5, fontWeight: 800, color: "var(--t2-white)", lineHeight: 1.3 }} data-testid="pdetail-name">
              {bc.name}
              {(tm.BrandName || tm.ProductLabel) && (
                <span className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey2)", fontWeight: 600, marginLeft: 8 }}>
                  {tm.BrandName || ""}{tm.BrandName && tm.ProductLabel ? " · " : ""}{tm.ProductLabel || ""}
                </span>
              )}
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} data-testid="product-detail-close" aria-label="Close product details"
          style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer", padding: 2, flexShrink: 0 }}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="product-detail-skeleton">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
            {Array.from({ length: 6 }).map((_, k) => <SkeletonBlock key={k} />)}
          </div>
          <SkeletonBlock h={90} />
        </div>
      ) : !product ? (
        <div data-testid="product-detail-notfound" style={{ fontSize: 12.5, color: "var(--t2-grey3)", padding: "14px 0", textAlign: "center" }}>
          Product record not found{sku ? ` for ${sku}` : ""}.
        </div>
      ) : (
        <>
          {/* 6 stat tiles — wrap 6 → 3 → 2 */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: attrs.length || datasheets.length || namedTags.length ? 16 : 0 }}>
            <Tile label="List sell ex VAT" value={GBP(sellEx)} />
            <Tile label="List sell inc VAT" value={GBP(sellInc)} />
            <Tile label="Cost" value={GBP(cost)} />
            <Tile label="List margin" value={listMargin != null ? `${listMargin.toFixed(1)}%` : "—"} accent="var(--t2-brass-ink)" />
            <Tile label="Inv level" value={bc.inventory_level ?? "—"} />
            <Tile label="Total sold" value={bc.total_sold ?? "—"} />
          </div>

          {attrs.length > 0 && (
            <div style={{ marginBottom: datasheets.length || namedTags.length ? 14 : 0 }}>
              <div style={{ fontSize: 9.5, color: "var(--t2-grey3)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 7 }}>Attributes</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "6px 20px" }} data-testid="pdetail-attrs">
                {attrs.map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11.5, padding: "3px 0", borderBottom: "1px solid var(--t2-border)" }}>
                    <span style={{ color: "var(--t2-grey3)", fontFamily: "var(--t2-mono)", fontSize: 10.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "55%" }} title={k}>{k}</span>
                    <span style={{ color: "var(--t2-grey1)", textAlign: "right", fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(datasheets.length > 0 || namedTags.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              {datasheets.map((f, k) => (
                <a key={k} href={String(f.value)} target="_blank" rel="noreferrer" data-testid={`pdetail-datasheet-${k}`}
                  className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999, border: "1px solid var(--t2-teal-ink)", color: "var(--t2-teal-ink)", textDecoration: "none" }}>
                  <FileText className="w-3 h-3" /> {f.name || "Datasheet"}
                </a>
              ))}
              {namedTags.slice(0, 10).map((t, k) => (
                <span key={`t-${k}`} className="t2-chip" style={{ fontSize: 10 }}>{t}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function LineItemsTable({ items, onChange, onAddCustom, pricingTier, onApplyTier, rules, isAdmin, onRulesChange, supplier, rebatePct, headerExtra, carriageCost = 0 }) {
  const carriage = Number(carriageCost) || 0;
  const [studioIdx, setStudioIdx] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);   // which row's product panel is expanded (one at a time)

  const hardPct = Number(rules?.hard_floor_pct ?? 3);
  const softPct = Number(rules?.soft_floor_pct ?? 6);
  const canTier = !!(pricingTier?.matched && !pricingTier.custom);
  const supplierSet = !!(supplier && supplier.id);
  const effRebate = supplier?.category === "Galaxy" ? (Number(rebatePct) || 0) : 0;
  const effOf = (cost) => effectiveCost(cost, effRebate);

  const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const remove = (i) => { if (openIdx === i) setOpenIdx(null); onChange(items.filter((_, idx) => idx !== i)); };
  const setUnit = (i, u) => {
    const it = items[i];
    const nextUnit = Number(u) || 0;
    // Custom lines: margin is on the raw cost (rebate is applied in the Margin Summary).
    if (it._custom) {
      update(i, { mode: "custom", unitPrice: nextUnit, _marginSet: false, margin: round2(marginFromUnit(nextUnit, it.cost)) });
      return;
    }
    update(i, { mode: "custom", unitPrice: nextUnit, margin: round2(marginFromUnit(nextUnit, effOf(it.cost))) });
  };
  // Custom-line margin % → reverse-engineer the unit price from cost.
  const setCustomMargin = (i, m) => {
    const it = items[i];
    const pct = Math.max(-95, Math.min(95, Number(m) || 0));
    update(i, { mode: "custom", margin: pct, _marginSet: true, unitPrice: round2(unitFromCostMargin(it.cost, pct)) });
  };
  // Custom-line cost: keep the typed margin (re-price) if the rep set one, else keep the price (re-margin).
  const setCustomCost = (i, c) => {
    const it = items[i];
    const cost = Math.max(0, Number(c) || 0);
    if (it._marginSet || !(Number(it.unitPrice) > 0)) {
      update(i, { cost, unitPrice: round2(unitFromCostMargin(cost, Number(it.margin) || 0)) });
    } else {
      update(i, { cost, margin: round2(marginFromUnit(it.unitPrice, cost)) });
    }
  };

  // Esc closes the open product panel.
  useEffect(() => {
    if (openIdx == null) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenIdx(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIdx]);

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0);

  return (
    <T2Panel
      icon={ClipboardList}
      title="Line items"
      testid="panel-lines"
      right={
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {pricingTier?.matched && (
            <span className="t2-mono" data-testid="lines-tier-badge"
              style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
                background: pricingTier.custom ? "rgba(139,92,246,0.15)" : "rgba(255,104,32,0.14)",
                border: `1px solid ${pricingTier.custom ? "rgba(139,92,246,0.4)" : "rgba(255,104,32,0.4)"}`,
                color: pricingTier.custom ? "var(--qb-disc)" : "var(--t2-orange-ink)" }}>
              {pricingTier.custom ? `${pricingTier.tier} · Custom` : `${pricingTier.tier} · −${pricingTier.disc}%`}
            </span>
          )}
          {headerExtra}
          <button type="button" onClick={() => setRulesOpen(true)} className="t2-btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} data-testid="btn-pricing-rules" title="Global margin floors">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Pricing rules
          </button>
          <button type="button" onClick={() => onApplyTier?.()} disabled={!canTier} className="t2-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 11, opacity: canTier ? 1 : 0.45, cursor: canTier ? "pointer" : "not-allowed" }}
            data-testid="btn-apply-tier-price" title={canTier ? "Reset every line to the customer's tier price" : "Pick a customer with a standard VIP tier first"}>
            <BadgePercent className="w-3.5 h-3.5" /> Apply tier price
          </button>
          <button type="button" onClick={onAddCustom} className="t2-btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }} data-testid="btn-add-custom-line" title="Add a non-stock or bespoke service line">
            <Plus className="w-3.5 h-3.5" /> Add custom item
          </button>
          <span>{items.length} row{items.length === 1 ? "" : "s"}</span>
        </div>
      }
    >
      {items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "26px 0" }}>
          <Package style={{ margin: "0 auto 8px", opacity: .3 }} className="w-8 h-8" />
          <div style={{ fontSize: 12, color: "var(--t2-grey3)" }}>No products added yet.</div>
          <div style={{ fontSize: 11, color: "var(--t2-grey4)", marginTop: 4 }}>Use the search bar above or press ⌘K.</div>
        </div>
      ) : (
        <div className="t2-tbl-wrap" style={{ background: "transparent", border: "none" }}>
          <table className="t2-tbl qb-lines-tbl" style={{ width: "100%", minWidth: 960, tableLayout: "fixed" }} data-testid="line-items-table">
            <colgroup>
              <col />{/* Product (name + SKU/brand + View product) — takes the remaining width */}
              <col style={{ width: 76 }} />{/* Avail */}
              <col style={{ width: 92 }} />{/* Cost */}
              <col style={{ width: 310 }} />{/* Pricing summary / custom margin */}
              <col style={{ width: 118 }} />{/* Unit £ */}
              <col style={{ width: 84 }} />{/* Qty */}
              <col style={{ width: 106 }} />{/* Total */}
              <col style={{ width: 30 }} />{/* trash */}
            </colgroup>
            <thead>
              <tr>
                <th>Product</th>
                <th style={{ textAlign: "center" }}>Avail.</th>
                <th style={{ textAlign: "right" }}>Cost</th>
                <th style={{ fontSize: 11 }}>Pricing</th>
                <th style={{ textAlign: "center" }}>Unit £</th>
                <th style={{ textAlign: "center" }}>Qty</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const isTest = !!it._lab_test_data;
                const isCustomLine = !!it._custom;
                const open = openIdx === i;
                const total = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
                const base = Number(it.basePrice) || Number(it.unitPrice) || 0;
                const unit = Number(it.unitPrice) || 0;
                const hasCost = Number(it.cost) > 0;
                const margin = hasCost ? marginFromUnit(unit, isCustomLine ? it.cost : effOf(it.cost)) : null;
                const marginAfterRebate = (isCustomLine && hasCost && effRebate > 0) ? marginFromUnit(unit, effOf(it.cost)) : null;
                const isTier = it.mode === "tier";
                const tierPrice = canTier ? tierUnitFromBase(base, pricingTier.disc) : null;
                const vsTier = (tierPrice && tierPrice > 0) ? ((unit - tierPrice) / tierPrice) * 100 : null;
                const band = margin != null ? bandOf(margin) : null;
                const bandTone = band ? { very_lean: "red", lean: "warn", healthy: "ok", great: "teal" }[band.key] : "";
                const edge = open ? "inset 3px 0 0 var(--t2-orange)" : "none";
                const rowBg = open ? "rgba(255,104,32,0.045)" : "transparent";
                return (
                  <Fragment key={`${it._id || it.sku || "line"}-${i}`}>
                    <tr data-testid={`line-row-${i}`} style={{ height: 78, background: rowBg }}>
                      {/* Product — name (2-line wrap) + meta (SKU · brand · View product) */}
                      <td style={{ verticalAlign: "top", paddingTop: 12, paddingBottom: 12, boxShadow: edge }}>
                        {isCustomLine ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <input className="t2-input" style={{ padding: "6px 8px", fontSize: 13, fontWeight: 700 }} value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Custom item description…" data-testid={`line-name-${i}`} />
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span className="t2-mono" style={{ fontSize: 8.5, fontWeight: 800, padding: "2px 6px", borderRadius: 4, letterSpacing: ".06em", background: "color-mix(in srgb, var(--t2-orange) 12%, transparent)", color: "var(--t2-orange-ink)" }}>CUSTOM</span>
                              <span className="t2-mono" style={{ fontSize: 9.5, fontWeight: 700, color: "var(--t2-grey3)", letterSpacing: ".06em" }}>SKU</span>
                              <input className="t2-input t2-mono" style={{ padding: "4px 8px", fontSize: 11, fontWeight: 600, textAlign: "left", maxWidth: 150 }}
                                value={it.sku || ""} onChange={(e) => update(i, { sku: e.target.value.toUpperCase() })} placeholder="optional" data-testid={`line-sku-${i}`} />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontWeight: 700, lineHeight: 1.3, fontSize: 13, color: "var(--t2-white)", wordBreak: "break-word" }} title={it.name} data-testid={`line-name-${i}`}>{it.name}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 7, flexWrap: "wrap" }}>
                              {it.sku && <span className="t2-mono" style={{ color: isTest ? "var(--t2-teal-ink)" : "var(--t2-orange-ink)", fontSize: 11, fontWeight: 700 }} data-testid={`line-sku-${i}`}>{it.sku}</span>}
                              {it._brand && <span className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 10.5 }}>{it._brand}</span>}
                              {isTest && <span className="t2-mono" style={{ fontSize: 8.5, color: "var(--t2-teal-ink)", textTransform: "uppercase", letterSpacing: ".08em" }}>test data</span>}
                              <button type="button" onClick={() => setOpenIdx(open ? null : i)} aria-expanded={open} data-testid={`line-view-product-${i}`}
                                title={open ? "Hide product details" : "View product details"}
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, cursor: "pointer",
                                  border: `1px solid ${open ? "var(--t2-orange)" : "var(--t2-border)"}`,
                                  background: open ? "var(--t2-orange)" : "transparent",
                                  color: open ? "#0b0f17" : "var(--t2-grey2)" }}>
                                <Info className="w-3 h-3" /> {open ? "Hide product" : "View product"}
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                      {/* Avail */}
                      <td style={{ textAlign: "center" }} data-testid={`line-avail-${i}`}>
                        <AvailabilityChip days={it.leadTimeDays} onChange={isCustomLine ? (v) => update(i, { leadTimeDays: v }) : null} testid={`avail-days-input-${i}`} />
                      </td>
                      {/* Cost */}
                      <td className="t2-mono" style={{ textAlign: "right", color: "var(--t2-grey1)", fontSize: 12.5, fontWeight: 600 }}>
                        {isCustomLine ? (
                          <StepBox value={it.cost ?? 0} onChange={(v) => setCustomCost(i, v)} step={0.1} decimals={2} testid={`line-cost-${i}`} />
                        ) : GBP(it.cost)}
                      </td>
                      {/* Pricing summary → opens Margin Studio. Custom lines get an editable margin % box. */}
                      <td>
                        {isCustomLine ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid={`line-custom-margin-cell-${i}`}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ flex: "0 1 110px", minWidth: 0 }} title={hasCost ? "Margin % on cost (profit ÷ price). Type a % to set the unit price." : "Enter a cost first to work out margin"}>
                                <StepBox value={hasCost ? (it._marginSet ? it.margin : round2(margin)) : 0} onChange={(v) => setCustomMargin(i, v)} step={1} min={-95} decimals={2} suffix="%" disabled={!hasCost} testid={`line-margin-${i}`} />
                              </div>
                              <span className={`t2-chip t2-mono ${bandTone}`} data-testid={`line-margin-chip-${i}`} style={{ fontSize: 9.5, fontWeight: 800, flexShrink: 0 }}
                                title={band ? band.label : "No cost on file — margin unknown"}>
                                {band ? band.label.replace(" Margin", "") : "no cost"}
                              </span>
                            </div>
                            <div className="t2-mono" style={{ display: "flex", justifyContent: "space-between", gap: 6, fontSize: 9.5, color: "var(--t2-grey3)" }}>
                              <span data-testid={`line-profit-${i}`}>{hasCost ? `Profit ${GBP((unit - Number(it.cost)) * (Number(it.qty) || 0))}` : "Add a cost to see margin"}</span>
                              {marginAfterRebate != null && <span data-testid={`line-margin-rebate-${i}`} title={`Including the ${effRebate}% supplier rebate`}>{round2(marginAfterRebate)}% after rebate</span>}
                            </div>
                          </div>
                        ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                         <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span className={`t2-chip t2-mono ${bandTone}`} data-testid={`line-margin-chip-${i}`}
                            style={{ fontSize: 12.5, fontWeight: 800, padding: "3px 9px", whiteSpace: "nowrap", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                            title={band ? band.label : "No cost on file — margin unknown"}>
                            {margin != null ? `${round2(margin)}% ${band.label.replace(" Margin", "")}` : "— no cost"}
                          </span>
                          {!isCustomLine && (
                            <span className="t2-mono" data-testid={`line-mode-tag-${i}`}
                              style={{ fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 4, letterSpacing: ".05em",
                                background: isTier ? "rgba(255,104,32,0.12)" : "rgba(20,184,166,0.12)", color: isTier ? "var(--t2-orange-ink)" : "var(--t2-teal-ink)" }}>
                              {isTier ? "TIER" : "CUSTOM"}
                            </span>
                          )}
                          <button type="button" onClick={() => setStudioIdx(i)} className="t2-btn-ghost"
                            style={{ padding: "4px 10px", fontSize: 11.5, marginLeft: "auto", flexShrink: 0 }} data-testid={`line-edit-price-${i}`} title="Open Margin Studio">
                            <Settings2 className="w-3 h-3" /> Edit
                          </button>
                         </div>
                         <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span className="t2-mono" style={{ fontSize: 11.5, color: vsTier == null ? "var(--t2-grey4)" : Math.abs(vsTier) < 0.05 ? "var(--t2-grey4)" : vsTier < 0 ? "var(--t2-teal-ink)" : "var(--t2-brass-ink)" }}>
                            {vsTier == null ? "no tier" : `vs tier ${vsTier > 0 ? "+" : ""}${round2(vsTier)}%`}
                          </span>
                          {lineWebDiscPct(it) != null && Math.abs(lineWebDiscPct(it)) >= 0.05 && (
                            <span className="t2-mono" data-testid={`line-web-disc-${i}`} title={`Website price ${GBP(Number(it.basePrice))} ex VAT`}
                              style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 7px", borderRadius: 4, color: "var(--qb-disc)",
                                background: "color-mix(in srgb, var(--qb-disc) 12%, transparent)" }}>
                              {lineWebDiscPct(it) > 0 ? `−${round2(lineWebDiscPct(it))}% vs web` : `+${round2(-lineWebDiscPct(it))}% vs web`}
                            </span>
                          )}
                         </div>
                        </div>
                        )}
                      </td>
                      {/* Unit £ */}
                      <td><StepBox value={it.unitPrice ?? 0} onChange={(v) => setUnit(i, v)} step={0.1} decimals={2} testid={`line-unit-${i}`} /></td>
                      {/* Qty */}
                      <td><StepBox value={it.qty ?? 1} onChange={(v) => update(i, { qty: v })} step={1} min={0} testid={`line-qty-${i}`} /></td>
                      {/* Total */}
                      <td className="t2-mono" style={{ textAlign: "right", color: "var(--t2-white)", fontWeight: 900, fontSize: total >= 100000 ? 12 : 14, whiteSpace: "nowrap" }} data-testid={`line-total-${i}`}>{GBP(total)}</td>
                      {/* trash */}
                      <td style={{ textAlign: "center" }}>
                        <button onClick={() => remove(i)} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }} data-testid={`line-remove-${i}`} aria-label="Remove line">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {open && !isCustomLine && (
                      <tr data-testid={`line-detail-row-${i}`}>
                        <td colSpan={8} style={{ padding: 0 }}>
                          <ProductDetailPanel sku={it.sku} onClose={() => setOpenIdx(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {items.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <div style={{ width: 260, fontSize: 12.5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "var(--t2-grey2)" }}>
              <span>Products (ex VAT)</span><span className="t2-mono">{GBP(subtotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "var(--t2-grey2)" }} data-testid="line-items-carriage-row">
              <span>Carriage</span><span className="t2-mono">{carriage > 0 ? GBP(carriage) : "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", color: "var(--t2-grey2)" }}>
              <span>VAT 20%</span><span className="t2-mono">{GBP((subtotal + carriage) * 0.2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", borderTop: "1px solid var(--t2-border)", marginTop: 4, color: "var(--t2-white)", fontWeight: 800, fontSize: 14 }}>
              <span>Total inc VAT</span>
              <span className="t2-mono" style={{ color: "var(--t2-orange-ink)" }} data-testid="line-items-total">{GBP((subtotal + carriage) * 1.2)}</span>
            </div>
          </div>
        </div>
      )}

      <MarginStudio
        open={studioIdx != null}
        line={studioIdx != null ? items[studioIdx] : null}
        tierInfo={pricingTier}
        rules={rules}
        rebatePct={effRebate}
        supplierSet={supplierSet}
        onApply={(patch) => { if (studioIdx != null) update(studioIdx, patch); }}
        onClose={() => setStudioIdx(null)}
      />
      <PricingRulesModal open={rulesOpen} isAdmin={isAdmin} onClose={() => setRulesOpen(false)} onSaved={(r) => onRulesChange?.(r)} />
    </T2Panel>
  );
}
